import { useMemo } from "react";
import clsx from "clsx";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { parseServerDateTime } from "@/lib/globalFormatterHelper";
import clockStore from "@/store/clockStore";
import featureFlagStore from "@/store/featureFlagStore";
import tradeDataStore from "@/store/tradeDataStore";
import {
  EXPIRED_LABEL,
  NO_EXPIRY_LABEL,
  isExpiryLive,
  returnExpiryDisplay,
  returnExpiryStepKey,
  returnStaticExpiryDisplay,
} from "./expiryCountdownHelpers";

// LQC-2678 - the live expiry countdown as read-only text, shared by the Edit
// Panel and the Trade Settings sidebar. Same tier discipline as the Expires
// cell: subscribe to the quantised step key, never nowMs, or every panel
// repaints once a second.
//
// Deliberately plainer than the cell: no tone tint (a coloured block reads as an
// actionable control in a settings panel), no hover tooltip (it would repeat the
// value already on screen), and the headings' grey rather than body text, since
// this is informational beside the controls it sits with.
const ExpiryCountdownText = ({
  esid,
  testId = "ExpiryCountdownText",
  className,
}: {
  esid: string | undefined;
  testId?: string;
  className?: string;
}) => {
  const tradeExpiryCountdown = featureFlagStore(
    (state) => state.tradeExpiryCountdown,
  );
  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade = currentTradeData.find((trade) => trade.esid === esid);

  // parseServerDateTime, not new Date: expiryEnd arrives without a timezone
  // designator, so a raw parse renders hours out.
  const rawExpiry = thisTrade?.expiryEnd;
  const expiryEnd = useMemo(() => parseServerDateTime(rawExpiry), [rawExpiry]);
  const isLive = isExpiryLive(thisTrade?.status);

  const stepKey = clockStore((state) =>
    tradeExpiryCountdown
      ? returnExpiryStepKey(expiryEnd, state.nowMs, isLive)
      : "countdownOff",
  );

  const display = useMemo(
    () =>
      tradeExpiryCountdown
        ? returnExpiryDisplay(expiryEnd, clockStore.getState().nowMs, isLive)
        : returnStaticExpiryDisplay(expiryEnd),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepKey, expiryEnd, isLive, tradeExpiryCountdown],
  );

  // Once the backend confirms the lapse the countdown is over, so the tiering
  // falls through to a bare timestamp. Say the word instead.
  const label =
    thisTrade?.status === ExecutionStrategyStatus.Expired
      ? EXPIRED_LABEL
      : display.label || NO_EXPIRY_LABEL;

  return (
    <span
      data-testid={testId}
      id={testId}
      data-expiry-tier={display.tier}
      className={clsx(
        "whitespace-nowrap text-gray-500 dark:text-gray-400",
        className,
      )}
    >
      {label}
    </span>
  );
};

export default ExpiryCountdownText;
